import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Avatar,
  Fade,
  Zoom
} from '@mui/material';
import {
  Download,
  Visibility,
  Description,
  InsertDriveFile,
  PictureAsPdf,
  Article,
  Image,
  VideoFile,
  AudioFile,
  Archive,
  Code,
  DataObject
} from '@mui/icons-material';
import { documentService } from '../services/api';

const DocumentList = forwardRef((props, ref) => {
  const { } = props;
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewDialog, setPreviewDialog] = useState({ open: false, document: null });

  const loadDocuments = async (searchTerm = '') => {
    setLoading(true);
    setError('');
    
    try {
      const result = searchTerm 
        ? await documentService.searchDocuments(searchTerm)
        : await documentService.getAllDocuments();
      setDocuments(result.documents || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const filterDocuments = (docs, regionFilter, csTypeFilter) => {
    return docs.filter(doc => {
      const regionMatch = regionFilter === 'All' || doc.region === regionFilter;
      const csTypeMatch = csTypeFilter === 'All' || doc.csType === csTypeFilter;
      return regionMatch && csTypeMatch;
    });
  };

  useImperativeHandle(ref, () => ({
    searchDocuments: loadDocuments,
    refreshDocuments: () => loadDocuments()
  }));

  useEffect(() => {
    loadDocuments();
  }, []);

  const handlePreview = (document) => {
    setPreviewDialog({ open: true, document });
  };

  const handleDownload = async (document) => {
    try {
      const { downloadUrl } = await documentService.getDownloadUrl(document.id);
      window.open(downloadUrl, '_blank');
    } catch (err) {
      alert('Failed to download document');
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const iconProps = { sx: { fontSize: 40 } };
    
    switch (ext) {
      case 'pdf': return <PictureAsPdf {...iconProps} sx={{ ...iconProps.sx, color: '#FF0000' }} />;
      case 'ppt': case 'pptx': return (
        <svg viewBox="0 0 512 512" width="40" height="40" fill="none">
          <path fill="#C43E1C" d="M438.558,512H19.786c-8.214,0-14.876-6.66-14.876-14.876V256.916c0-8.216,6.661-14.876,14.876-14.876s14.876,6.66,14.876,14.876v225.332h389.021v-32.833c0-8.216,6.661-14.876,14.876-14.876c8.214,0,14.876,6.66,14.876,14.876v47.709C453.434,505.34,446.773,512,438.558,512z"/>
          <polygon fill="#F9B28B" points="19.786,177.122 19.786,172.332 175.581,14.876 175.581,177.122"/>
          <rect x="196.155" y="219.435" fill="#F9B28B" width="296.061" height="163.65"/>
          <path fill="#C43E1C" d="M492.216,204.559h-38.782V14.876C453.434,6.66,446.773,0,438.558,0H175.581c-0.18,0-0.359,0.021-0.538,0.027c-0.171,0.006-0.341,0.013-0.51,0.025c-0.641,0.046-1.278,0.118-1.903,0.244c-0.015,0.003-0.03,0.007-0.045,0.012c-0.617,0.126-1.22,0.305-1.813,0.507c-0.155,0.054-0.308,0.109-0.46,0.167c-0.589,0.223-1.166,0.472-1.723,0.768c-0.016,0.009-0.033,0.015-0.048,0.022c-0.57,0.306-1.113,0.663-1.641,1.043c-0.134,0.095-0.263,0.193-0.394,0.295c-0.522,0.403-1.029,0.829-1.499,1.303L9.21,161.868c-0.35,0.353-0.678,0.721-0.988,1.104c-0.207,0.254-0.388,0.521-0.576,0.784c-0.092,0.131-0.195,0.256-0.283,0.388c-0.213,0.323-0.403,0.656-0.588,0.991c-0.048,0.085-0.103,0.167-0.147,0.253c-0.181,0.344-0.339,0.695-0.491,1.047c-0.039,0.089-0.085,0.174-0.12,0.263c-0.138,0.338-0.253,0.68-0.364,1.025c-0.037,0.116-0.083,0.229-0.119,0.345c-0.094,0.315-0.165,0.634-0.238,0.954c-0.036,0.155-0.079,0.305-0.11,0.461c-0.057,0.292-0.091,0.586-0.131,0.881c-0.025,0.186-0.06,0.37-0.077,0.558c-0.027,0.286-0.031,0.573-0.043,0.86c-0.007,0.183-0.027,0.364-0.027,0.547v4.792c0,8.216,6.661,14.876,14.876,14.876H175.58c8.214,0,14.876-6.66,14.876-14.876V29.752h233.226v174.807H196.156c-8.214,0-14.876,6.66-14.876,14.876v163.644c0,8.216,6.661,14.876,14.876,14.876h296.059c8.215,0,14.876-6.66,14.876-14.876V219.435C507.092,211.219,500.43,204.559,492.216,204.559z M160.705,162.246H50.692l56.297-56.896l53.718-54.29v111.186H160.705z M477.34,368.203H211.032V234.311H477.34V368.203z"/>
          <path fill="#C43E1C" d="M248.835,259.784c0-3.124,2.874-5.873,7.248-5.873h25.866c16.494,0,29.489,7.747,29.489,28.74v0.625c0,20.993-13.494,28.99-30.738,28.99h-12.371v26.991c0,3.999-4.873,5.998-9.747,5.998c-4.873,0-9.747-1.999-9.747-5.998V259.784z M268.328,270.906v26.366h12.371c6.996,0,11.246-3.999,11.246-12.496v-1.375c0-8.497-4.249-12.496-11.246-12.496L268.328,270.906z"/>
          <path fill="#C43E1C" d="M319.935,259.784c0-3.124,2.874-5.873,7.248-5.873h25.866c16.494,0,29.489,7.747,29.489,28.74v0.625c0,20.993-13.494,28.99-30.738,28.99h-12.371v26.991c0,3.999-4.873,5.998-9.747,5.998s-9.747-1.999-9.747-5.998L319.935,259.784z M339.428,270.906v26.366h12.371c6.996,0,11.246-3.999,11.246-12.496v-1.375c0-8.497-4.249-12.496-11.246-12.496L339.428,270.906z"/>
          <path fill="#C43E1C" d="M445.139,253.911c4.122,0,5.873,4.498,5.873,8.622c0,4.748-2.126,8.872-5.873,8.872h-17.244v67.852c0,3.999-4.873,5.998-9.748,5.998c-4.872,0-9.747-1.999-9.747-5.998v-67.852h-17.244c-3.749,0-5.873-4.124-5.873-8.872c0-4.124,1.751-8.622,5.873-8.622H445.139z"/>
        </svg>
      );
      case 'doc': case 'docx': return <Article {...iconProps} sx={{ ...iconProps.sx, color: '#00ACC1' }} />;
      case 'xls': case 'xlsx': return <DataObject {...iconProps} sx={{ ...iconProps.sx, color: '#8DB600' }} />;
      case 'jpg': case 'jpeg': case 'png': case 'gif': return <Image {...iconProps} sx={{ ...iconProps.sx, color: '#A333C8' }} />;
      case 'mp4': case 'avi': case 'mov': return <VideoFile {...iconProps} sx={{ ...iconProps.sx, color: '#3B1F5B' }} />;
      case 'mp3': case 'wav': case 'flac': return <AudioFile {...iconProps} sx={{ ...iconProps.sx, color: '#C2185B' }} />;
      case 'zip': case 'rar': case '7z': return <Archive {...iconProps} sx={{ ...iconProps.sx, color: '#7B68AE' }} />;
      case 'js': case 'html': case 'css': case 'py': case 'java': return <Code {...iconProps} sx={{ ...iconProps.sx, color: '#00ACC1' }} />;
      default: return <InsertDriveFile {...iconProps} sx={{ ...iconProps.sx, color: '#7B68AE' }} />;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    return new Date(dateString).toLocaleDateString();
  };

  const extractTags = (document) => {
    return document.tags || [];
  };

  return (
    <Box>
      <Paper 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(135deg, #E0E0E0 0%, #f5f5f5 100%)',
          borderRadius: '24px',
          p: 4,
          mb: 3,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(45deg, #3B1F5B 30%, #A333C8 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1
              }}
            >
              AWS Case Studies
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {documents.length} case studies 
            </Typography>
          </Box>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress 
              sx={{ 
                color: '#A333C8',
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round'
                }
              }} 
            />
          </Box>
        )}

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: '16px',
              backgroundColor: 'rgba(194, 24, 91, 0.1)',
              border: '1px solid rgba(194, 24, 91, 0.2)'
            }}
          >
            {error}
          </Alert>
        )}

        {!loading && documents.length === 0 && (
          <Box sx={{ textAlign: 'center', p: 6 }}>
            <Description sx={{ fontSize: 80, color: '#7B68AE', mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No documents found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload some documents to get started or try a different search term.
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))' }}>
          {documents.map((document, index) => {
            const chips = [
              document.companyName && { label: document.companyName, color: '#3B1F5B' },
              document.sector && { label: document.sector, color: '#00695C' },
              document.region && { label: document.region, color: '#1565C0' },
              document.csType && { label: document.csType, color: '#C2185B' }
            ].filter(Boolean);
            
            return (
              <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }} key={document.id}>
                <Card
                  sx={{
                    borderRadius: '20px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(163, 51, 200, 0.1)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 60px rgba(59, 31, 91, 0.15)',
                      borderColor: 'rgba(163, 51, 200, 0.3)',
                      '& .document-actions': {
                        opacity: 1,
                        transform: 'translateY(0)'
                      }
                    }
                  }}
                  onClick={() => handlePreview(document)}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                        {getFileIcon(document.name)}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            fontWeight: 600,
                            mb: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: '#1E1E1E'
                          }}
                        >
                          {document.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {formatFileSize(document.size)} • {formatDate(document.lastModified)}
                        </Typography>
                      </Box>
                    </Box>

                    {chips.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {chips.map((chip, i) => (
                            <Chip
                              key={i}
                              label={chip.label}
                              size="small"
                              sx={{
                                backgroundColor: `${chip.color}18`,
                                color: chip.color,
                                fontWeight: 500,
                                borderRadius: '12px',
                                '& .MuiChip-label': { px: 1.5 }
                              }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}

                    <Box 
                      className="document-actions"
                      sx={{
                        display: 'flex',
                        gap: 1,
                        opacity: 0,
                        transform: 'translateY(10px)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Visibility />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePreview(document);
                        }}
                        sx={{
                          borderRadius: '12px',
                          textTransform: 'none',
                          fontWeight: 500,
                          borderColor: '#A333C8',
                          color: '#A333C8',
                          '&:hover': {
                            borderColor: '#3B1F5B',
                            backgroundColor: 'rgba(163, 51, 200, 0.1)'
                          }
                        }}
                      >
                        Preview
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Download />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(document);
                        }}
                        sx={{
                          borderRadius: '12px',
                          textTransform: 'none',
                          fontWeight: 500,
                          background: 'linear-gradient(45deg, #00ACC1 30%, #0097A7 90%)',
                          boxShadow: 'none',
                          '&:hover': {
                            boxShadow: '0 4px 20px rgba(0, 172, 193, 0.4)'
                          }
                        }}
                      >
                        Download
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Zoom>
            );
          })}
        </Box>
      </Paper>

      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, document: null })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            background: 'linear-gradient(135deg, #f5f5f5 0%, #E0E0E0 100%)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
              {previewDialog.document && getFileIcon(previewDialog.document.name)}
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1E1E1E' }}>
                {previewDialog.document?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Document Preview
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Size: {formatFileSize(previewDialog.document?.size)}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Last Modified: {formatDate(previewDialog.document?.lastModified)}
            </Typography>
            {previewDialog.document && (
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {previewDialog.document.companyName && (
                    <Chip label={previewDialog.document.companyName} size="small" sx={{ background: 'linear-gradient(45deg, #3B1F5B 30%, #A333C8 90%)', color: 'white', fontWeight: 500, borderRadius: '12px' }} />
                  )}
                  {previewDialog.document.sector && (
                    <Chip label={previewDialog.document.sector} size="small" sx={{ background: 'linear-gradient(45deg, #00695C 30%, #00897B 90%)', color: 'white', fontWeight: 500, borderRadius: '12px' }} />
                  )}
                  {previewDialog.document.region && (
                    <Chip label={previewDialog.document.region} size="small" sx={{ background: 'linear-gradient(45deg, #1565C0 30%, #1E88E5 90%)', color: 'white', fontWeight: 500, borderRadius: '12px' }} />
                  )}
                  {previewDialog.document.csType && (
                    <Chip label={previewDialog.document.csType} size="small" sx={{ background: 'linear-gradient(45deg, #C2185B 30%, #E91E63 90%)', color: 'white', fontWeight: 500, borderRadius: '12px' }} />
                  )}
                </Stack>
              </Box>
            )}
          </Box>
          
          {previewDialog.document?.webUrl && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                href={previewDialog.document.webUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 500,
                  borderColor: '#A333C8',
                  color: '#A333C8'
                }}
              >
                Open in AWS Case Studies
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setPreviewDialog({ open: false, document: null })}
            sx={{ 
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 500,
              color: '#7B68AE'
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => handleDownload(previewDialog.document)}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 500,
              background: 'linear-gradient(45deg, #00ACC1 30%, #0097A7 90%)',
              boxShadow: '0 4px 20px rgba(0, 172, 193, 0.3)'
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default DocumentList;
